/**
 * Performance monitoring utilities for animations and loading times
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      animationFrames: [],
      loadTimes: {},
      memoryUsage: [],
    };
    this.isMonitoring = false;
    this.frameCount = 0;
    this.lastFrameTime = 0;
  }

  /**
   * Start monitoring animation frame rates
   */
  startAnimationMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.frameCount = 0;
    this.lastFrameTime = performance.now();
    
    const monitorFrame = (timestamp) => {
      if (!this.isMonitoring) return;
      
      const deltaTime = timestamp - this.lastFrameTime;
      
      // Ensure deltaTime is positive to avoid Infinity or negative FPS
      if (deltaTime > 0) {
        const fps = 1000 / deltaTime;
        
        this.metrics.animationFrames.push({
          timestamp,
          fps: Math.round(fps),
          deltaTime: Math.round(deltaTime * 100) / 100,
        });
        
        // Keep only last 100 frames
        if (this.metrics.animationFrames.length > 100) {
          this.metrics.animationFrames.shift();
        }
        
        this.frameCount++;
      }
      
      this.lastFrameTime = timestamp;
      
      requestAnimationFrame(monitorFrame);
    };
    
    requestAnimationFrame(monitorFrame);
  }

  /**
   * Stop monitoring animation frame rates
   */
  stopAnimationMonitoring() {
    this.isMonitoring = false;
  }

  /**
   * Get average FPS over the last monitoring period
   */
  getAverageFPS() {
    if (this.metrics.animationFrames.length === 0) return 0;
    
    const totalFPS = this.metrics.animationFrames.reduce((sum, frame) => sum + frame.fps, 0);
    return Math.round(totalFPS / this.metrics.animationFrames.length);
  }

  /**
   * Get minimum FPS (worst performance)
   */
  getMinFPS() {
    if (this.metrics.animationFrames.length === 0) return 0;
    
    return Math.min(...this.metrics.animationFrames.map(frame => frame.fps));
  }

  /**
   * Check if animations are performing well (>= 30 FPS)
   */
  isPerformanceGood() {
    const avgFPS = this.getAverageFPS();
    const minFPS = this.getMinFPS();
    
    return avgFPS >= 30 && minFPS >= 20;
  }

  /**
   * Measure loading time for a specific operation
   */
  startLoadTimeTracking(operationName) {
    this.metrics.loadTimes[operationName] = {
      startTime: performance.now(),
      endTime: null,
      duration: null,
    };
  }

  /**
   * End loading time tracking for an operation
   */
  endLoadTimeTracking(operationName) {
    if (!this.metrics.loadTimes[operationName]) {
      console.warn(`No start time found for operation: ${operationName}`);
      return;
    }
    
    const endTime = performance.now();
    const startTime = this.metrics.loadTimes[operationName].startTime;
    const duration = endTime - startTime;
    
    this.metrics.loadTimes[operationName] = {
      ...this.metrics.loadTimes[operationName],
      endTime,
      duration: Math.round(duration * 100) / 100,
    };
    
    return duration;
  }

  /**
   * Monitor memory usage (if available)
   */
  trackMemoryUsage() {
    if (!performance.memory) {
      console.warn('Memory monitoring not available in this browser');
      return;
    }
    
    const memoryInfo = {
      timestamp: performance.now(),
      usedJSHeapSize: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024 * 100) / 100, // MB
      totalJSHeapSize: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024 * 100) / 100, // MB
      jsHeapSizeLimit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024 * 100) / 100, // MB
    };
    
    this.metrics.memoryUsage.push(memoryInfo);
    
    // Keep only last 50 measurements
    if (this.metrics.memoryUsage.length > 50) {
      this.metrics.memoryUsage.shift();
    }
    
    return memoryInfo;
  }

  /**
   * Get performance report
   */
  getPerformanceReport() {
    const report = {
      animations: {
        averageFPS: this.getAverageFPS(),
        minimumFPS: this.getMinFPS(),
        frameCount: this.frameCount,
        isPerformanceGood: this.isPerformanceGood(),
      },
      loadTimes: { ...this.metrics.loadTimes },
      memory: {
        current: this.trackMemoryUsage(),
        history: [...this.metrics.memoryUsage],
      },
      recommendations: this.getPerformanceRecommendations(),
    };
    
    return report;
  }

  /**
   * Get performance recommendations based on metrics
   */
  getPerformanceRecommendations() {
    const recommendations = [];
    const avgFPS = this.getAverageFPS();
    const minFPS = this.getMinFPS();
    
    if (avgFPS < 30) {
      recommendations.push('Consider reducing animation complexity or enabling reduced motion mode');
    }
    
    if (minFPS < 20) {
      recommendations.push('Animations may be causing frame drops - consider optimizing keyframes');
    }
    
    // Check load times
    Object.entries(this.metrics.loadTimes).forEach(([operation, timing]) => {
      if (timing.duration && timing.duration > 1000) {
        recommendations.push(`${operation} is taking too long (${timing.duration}ms) - consider optimization`);
      }
    });
    
    // Check memory usage
    if (performance.memory && this.metrics.memoryUsage.length > 0) {
      const latestMemory = this.metrics.memoryUsage[this.metrics.memoryUsage.length - 1];
      const memoryUsagePercent = (latestMemory.usedJSHeapSize / latestMemory.jsHeapSizeLimit) * 100;
      
      if (memoryUsagePercent > 80) {
        recommendations.push('High memory usage detected - consider reducing animation complexity');
      }
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Performance looks good! 🚀');
    }
    
    return recommendations;
  }

  /**
   * Log performance report to console (development only)
   */
  logPerformanceReport() {
    if (process.env.NODE_ENV !== 'development') return;
    
    const report = this.getPerformanceReport();
    
    console.group('🚀 Performance Report');
    console.log('📊 Animation Performance:', report.animations);
    console.log('⏱️ Load Times:', report.loadTimes);
    console.log('💾 Memory Usage:', report.memory.current);
    console.log('💡 Recommendations:', report.recommendations);
    console.groupEnd();
  }

  /**
   * Auto-adjust animation quality based on performance
   */
  autoAdjustQuality() {
    const avgFPS = this.getAverageFPS();
    const minFPS = this.getMinFPS();
    
    // If performance is poor, suggest reduced motion
    if (avgFPS < 25 || minFPS < 15) {
      // Add a CSS class to reduce animation complexity
      document.documentElement.classList.add('performance-mode');
      
      // Dispatch custom event for components to react
      window.dispatchEvent(new CustomEvent('performance-mode-enabled', {
        detail: { avgFPS, minFPS }
      }));
      
      console.warn('Performance mode enabled due to low FPS');
    } else {
      // Remove performance mode if FPS improves
      document.documentElement.classList.remove('performance-mode');
      
      window.dispatchEvent(new CustomEvent('performance-mode-disabled', {
        detail: { avgFPS, minFPS }
      }));
    }
  }

  /**
   * Clean up monitoring
   */
  cleanup() {
    this.stopAnimationMonitoring();
    this.metrics = {
      animationFrames: [],
      loadTimes: {},
      memoryUsage: [],
    };
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  performanceMonitor.startAnimationMonitoring();
  
  // Log performance report every 10 seconds
  setInterval(() => {
    performanceMonitor.logPerformanceReport();
    performanceMonitor.autoAdjustQuality();
  }, 10000);
}

export default performanceMonitor;